/**
 * Created by Ning Jiang on 2021-06-03.
 */

import {TombstoneRip} from "../TombstoneRip";
import {TombstoneRipSymbolConfig, TombstoneRipSymbolState} from "./TombstoneRipSymbolConfig";
import {IParsedStackedSymbolName, StackedSymbol} from "../../core/reel/stackedsymbol/StackedSymbol";
import {IBaseSymbol} from "../../core/symbol/BaseSymbol";
import {gsap, TimelineLite} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {Layers, TombstoneRipLayers} from "../view/TombstoneRipLayers";
import {TombstoneRipGameAssets} from "../TombstoneRipGameAssets";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {MultiSprites} from "../../core/animation/MultiSprites";
import {SpineHelper} from "../../core/utils/SpineHelper";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";

export class TombstoneRipSymbol extends PIXI.Container implements IBaseSymbol {
    public symbolWidth:number = 134;
    public symbolHeight:number = 184;
    public bottomPadding:number = 0;
    protected _ripAssetName:string = "initialWin/xRip";
    protected _noWinTint:number = 0x888888;
    protected _noWinHintTint:number = 0xbbbbbb;

    protected _splitSplashDelay:number = 0.1;
    protected _splitSymbolAnimationDuration:number = 0.134;
    public splitSymbolDelay:number = 0.5;

    public symbolName:string;
    public stackedSymbolName:IParsedStackedSymbolName;
    public reelId:number;
    public symbolSize:number = 1;
    public isHighSymbol:boolean;
    public superMColor:string;

    protected _parts:MultiSprites[];
    protected _centerPosition:PIXI.Point;
    protected _rip:TimelineSprite;
    protected _midFireContainer:PIXI.Container;
    protected _midFire:PIXI.spine.Spine;
    // protected _midFireSmoke:TimelineSprite;
    protected _wildPatchContainer:PIXI.Container;
    protected _wildPatch:PIXI.spine.Spine;
    protected _wildPatchGlow:PIXI.spine.Spine;
    protected _scatterFrame:PIXI.Sprite;
    protected _superFSMultiplier:PIXI.BitmapText;
    protected _splitBloodContainer:PIXI.Container;
    private _idle:PIXI.spine.Spine | null;
    private _idleTl:TimelineLite | null;
    private _idlePlaying:boolean = false;

    public get symbolStep():number {
        return this.symbolHeight + this.bottomPadding;
    }

    constructor(symbolName:string, reelId:number, initState:TombstoneRipSymbolState, superMColor:string = "") {
        super();
        this.symbolName = symbolName;
        this.stackedSymbolName = StackedSymbol.parseStackedSymbolName(symbolName, reelId);
        this.reelId = reelId;
        this.isHighSymbol = TombstoneRipSymbolConfig.SYMBOLS[this.stackedSymbolName.symName].highSymbol === true;
        if(TombstoneRipSymbolConfig.SYMBOLS[symbolName]?.symbolHeightFactor) {
            this.symbolHeight *= TombstoneRipSymbolConfig.SYMBOLS[symbolName].symbolHeightFactor!;
        }
        this.superMColor = superMColor;
        this.initAnimation();
        this.getStateAnimation(initState, {duration : 0}, {duration : 0});
    }

    protected initAnimation():void {
        this._centerPosition = this.getCenterPosition();
        this._parts = [];
        const part = new MultiSprites();
        part.position.set(this._centerPosition.x, this._centerPosition.y);
        this.addChild(part);
        this._parts.push(part);

        if(this.reelId != 4 && this.superMColor === "_C3") {
            this.isHighSymbol = true;
            this._midFireContainer = new PIXI.Container();
            this._midFire = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("superMFire"));
            this._midFire.autoUpdate = true;
            this._midFireContainer.addChild(this._midFire);
            this._midFire.state.setAnimation(0, "loop", true);
        }
    }

    protected getCenterPosition():PIXI.Point {
        return new PIXI.Point(0, 0);
    }

    public getStateAnimation(state:TombstoneRipSymbolState, inVars:gsap.TweenVars, outVars:gsap.TweenVars, frames?:number[]):TimelineLite {
        // TODO: check the state config?
        this.createState(state);
        const tl = new TimelineLite();
        this._parts.forEach(part => {
            tl.add(part.playSprite(state, inVars, outVars, frames), 0);
        });

        const tintDuration = (state === TombstoneRipSymbolState.NO_WIN || state === TombstoneRipSymbolState.NO_WIN_HINT) ? inVars.duration : outVars.duration;
        const tint = this.getStateTint(state);
        const tintSaturation = tint / 0xffffff;
        tl.add(this.getStateColorChangeAnimation(tint, tintSaturation, <number>tintDuration), 0);

        return tl;
    }

    protected getStateColorChangeAnimation(tint:number, tintSaturation:number, tintDuration:number):TimelineLite {
        // No need to tint wildPath since it can only be no win state in reel 5 inside the reel frame, not inside symbol.
        const tl = new TimelineLite();
        if(this._superFSMultiplier) {
            tl.add(() => {this._superFSMultiplier.tint = tint}, 0);
        }
        if(this._splitBloodContainer) {
            tl.add(gsap.to(this._splitBloodContainer, {
                duration : tintDuration,
                pixi : {saturation : tintSaturation}
            }), 0);
        }
        if(this._scatterFrame) {
            tl.add(() => {this._scatterFrame.tint = tint}, 0);
        }

        return tl;
    }

    protected createState(state:TombstoneRipSymbolState):void {
        let updateLayers:boolean = false;
        this._parts.forEach(part => {
            if(!part.sprites[state]) {
                part.addSprites({
                    name : state,
                    textures : this.getStateTexture(state),
                    anchor : new PIXI.Point(0.5, 0.5)
                });
                if(state === TombstoneRipSymbolState.NO_WIN) {
                    part.sprites[state].tint = this._noWinTint;
                }
                if(state === TombstoneRipSymbolState.NO_WIN_HINT) {
                    part.sprites[state].tint = this._noWinHintTint;
                }
                updateLayers = true;
            }
        })
        if(updateLayers) {
            this.updateLayers();
        }
    }

    protected updateLayers():void {
        if(this._idle) {
            this.addChild(this._idle);
        }
        if(this._midFireContainer) {
            this.addChild(this._midFireContainer);
        }
        if(this._wildPatchContainer) {
            this.addChild(this._wildPatchContainer);
        }
        if(this._superFSMultiplier) {
            this.addChild(this._superFSMultiplier);
        }
        if(this._scatterFrame) {
            this.addChild(this._scatterFrame);
        }
        if(this._splitBloodContainer) {
            this.addChild(this._splitBloodContainer);
        }
        if(this._rip) {
            this.addChild(this._rip);
        }
    }

    protected getStateTexture(state?:TombstoneRipSymbolState):PIXI.Texture[] {
        let texturePath = `${this.symbolName}/${this.symbolName}`;
        if(this.symbolSize > 1) {
            texturePath += `_S${this.symbolSize}`;
        }
        if(state === TombstoneRipSymbolState.SPIN || state === TombstoneRipSymbolState.LANDING) {
            texturePath += `_${state}`;
        }
        texturePath += this.superMColor;

        if(!!SlotGame.apiPlugin.gameClientConfiguration.useCensoredGfxUS && this.symbolName === "M5D" && state != TombstoneRipSymbolState.SPIN) {
            texturePath += `_WV`;
        }

        return TombstoneRip.assetLoader.getTextures(texturePath);
    }

    public onRemove():void {
        if(this._wildPatch) {
            SpineHelper.destroySpine(this._wildPatch);
        }
        if(this._wildPatchGlow) {
            SpineHelper.destroySpine(this._wildPatchGlow);
        }
        if(this._idleTl) {
            this._idleTl.pause();
            this._idleTl.kill();
            this._idleTl = null;
        }
        if(this._idle) {
            SpineHelper.destroySpine(this._idle);
        }
        this.destroy({children : true});
    }

    // type = 0 reel split, type = 1 xSplit
    public split(type:number, noWin:boolean):TimelineLite {
        const tl = new TimelineLite();

        if(TombstoneRip.model.parsedGameData.convertScatterToReelSplit[this.reelId] > -1 && type === 1) {
            tl.add(() => {this.showScatterFrame(true)});
            tl.add(gsap.to(this, {duration : 0.1}), 0);
            return tl;
        }

        if(!this._splitBloodContainer) {
            this._splitBloodContainer = new PIXI.Container();
            this.addChild(this._splitBloodContainer);
        }
        const oldSize = this.symbolSize;
        this.symbolSize *= 2;
        this.addSplitBloodAnimation(tl, oldSize, type);
        this.addSplitSymbolAnimation(tl, oldSize, type);
        this.updateLayers();
        return tl;
    }

    protected addSplitBloodAnimation(tl:TimelineLite, oldSymbolSize:number, type:number, showSplash:boolean = true):void {
        const layer = TombstoneRipLayers.getLayer(Layers.WIN_PRESENTATIONS)!;
        const basePosition = this.getSplitBasePosition();
        const stagePosition = this.getStagePosition();
        const positions = oldSymbolSize === 1 ?
            [basePosition] :
            [new PIXI.Point(basePosition.x, basePosition.y - this.symbolHeight * 0.25), new PIXI.Point(basePosition.x, basePosition.y + this.symbolHeight * 0.25)];
        for(let i = 0; i < oldSymbolSize; i++) {
            const splash = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset(`${type === 0 ? "WRS" : "WXS"}Split`));
            splash.autoUpdate = true;
            splash.position.set(positions[i].x + stagePosition.x, positions[i].y + stagePosition.y);
            splash.visible = false;
            if(showSplash) {
                layer.addChild(splash);
                tl.add([
                    () => {splash.visible = true},
                    new SpineTween(splash, 0, "splitting")
                ], 0);
            }
            tl.add(() => {
                splash.position.set(positions[i].x, positions[i].y);
                this._splitBloodContainer.addChild(splash);
                splash.visible = true;
                new SpineTween(splash, 0, "splitDone");
                if(!showSplash) {
                    splash.state.timeScale = 3;
                }
            });
        }
    }

    protected addSplitSymbolAnimation(tl:TimelineLite, oldSymbolSize:number, type:number):void {
        const oldParts = this._parts.concat();
        this._parts = [];
        for(let i = 0; i < this.symbolSize; i++) {
            const part = new MultiSprites();
            part.y = -this.symbolHeight * 0.5 + (i + 0.5) * this.symbolHeight / this.symbolSize;
            part.alpha = 0;
            this.addChild(part);
            this._parts.push(part);
        }
        tl.add(this.getStateAnimation(TombstoneRipSymbolState.NORMAL, {duration : 0, alpha : 1}, {
            duration : 0,
            alpha : 0
        }), 0);
        oldParts.forEach(part => {
            tl.add(() => {
                this.removeChild(part);
                part.destroy();
            }, this._splitSplashDelay + this._splitSymbolAnimationDuration);
        });

        if(TombstoneRip.model.parsedGameData.convertScatterToReelSplit[this.reelId] > -1 && type === 0) {
            tl.add(() => {this.showScatterFrame()}, this._splitSplashDelay);
        }

        this._parts.forEach((part, index) => {
            if(type === 0) { // axe
                tl.add([
                        () => {
                            part.y = index === 0 ? -30 : -15;
                            part.alpha = index === 0 ? 1 : 0;
                        },
                        gsap.to(part, {duration : this._splitSymbolAnimationDuration, alpha : 1, y : part.y})
                    ],
                    this._splitSplashDelay);
            } else { //dagger
                const partTl = gsap.timeline();
                partTl.to(part, {x : 3, duration : 0.033})
                    .to(part, {x : 5, duration : 0.433})
                    .to(part, {x : 0, duration : 0.1});
                tl.add([
                    () => {part.alpha = 1},
                    this.getPartDaggerSplitAnimation(part)
                ], this._splitSplashDelay);
            }
        });
    }

    protected getPartDaggerSplitAnimation(part:PIXI.Container):TimelineLite {
        const partTl = new TimelineLite();
        partTl.add(gsap.to(part, {x : 3, duration : 0.033}));
        partTl.add(gsap.to(part, {x : 5, duration : 0.433}));
        partTl.add(gsap.to(part, {x : 0, duration : 0.1}));
        return partTl;
    }

    public getStagePosition():PIXI.Point {
        if(this.parent) {
            return new PIXI.Point(this.position.x + this.parent.x,
                this.position.y + this.parent.y);
        }
        return this.position;
    }

    protected getSplitBasePosition():PIXI.Point {
        return new PIXI.Point(0, 0);
    }

    public playRipAnimation():TimelineLite {
        if(!this._rip) {
            this._rip = new TimelineSprite(TombstoneRip.assetLoader.getTextures(this._ripAssetName));
            this._rip.anchor.set(0.5);
            this._rip.position.set(this._centerPosition.x, this._centerPosition.y);
            this._rip.hide();
            this.updateLayers();
        }

        return this._rip.getAnimationAutoShowHide(true, false);
    }

    public setWild(animate:boolean = true, patchName:string = ""):TimelineLite {
        patchName = patchName.length > 0 ? patchName : this.superMColor;
        if(!this._wildPatchContainer) {
            this._wildPatchContainer = new PIXI.Container();
            this.updateLayers();
        }
        if(!this._wildPatch) {
            this._wildPatch = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("superMWild"));
            this._wildPatch.autoUpdate = true;
            this._wildPatch.position.set(this._centerPosition.x, this._centerPosition.y + 4);
            this._wildPatch.visible = false;
            this._wildPatchContainer.addChild(this._wildPatch);
        }
        if(!this._wildPatchGlow) {
            this._wildPatchGlow = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("superMWildBlend"));
            this._wildPatchGlow.autoUpdate = true;
            this._wildPatchGlow.position.set(this._centerPosition.x, this._centerPosition.y + 4);
            this._wildPatchGlow.visible = false;
            this._wildPatchContainer.addChild(this._wildPatchGlow);
        }
        const tl = new TimelineLite();
        tl.add(() => {
                let entry = this._wildPatch.state.setAnimation(0, patchName, false);
                this._wildPatch.visible = true;
                if(animate) {
                    this._wildPatchGlow.state.setAnimation(0, "glow", false);
                    this._wildPatchGlow.visible = true;
                } else {
                    entry.trackTime = entry.trackEnd;
                }
            }
        );
        if(animate) {
            tl.add(gsap.to(this, {duration : this._wildPatch.spineData.findAnimation(patchName).duration}), 0);
        }

        return tl;
    }

    public setMultiplier(value:number, playFire:boolean):TimelineLite {
        if(!this._superFSMultiplier) {
            this._superFSMultiplier = new PIXI.BitmapText("", {
                fontName : TombstoneRipGameAssets.BITMAP_FONT_SUPERFS_GLOW_MUL,
                fontSize : 20,
                tint : this.getStateTint()
            });
            this._superFSMultiplier.letterSpacing = -14;
            (<PIXI.Point>this._superFSMultiplier.anchor).set(1, 2);
            this._superFSMultiplier.position.set(this.symbolWidth * 0.4, this.symbolHeight * 0.4);
            this._superFSMultiplier.visible = false;

            this.updateLayers();
        }
        const tl = new TimelineLite();
        tl.add([
            () => {
                this._superFSMultiplier.text = `x${value}`;
                this._superFSMultiplier.visible = true;
            },
            gsap.fromTo(this._superFSMultiplier.scale, {x : 1.5, y : 1.5}, {duration : 0.5, x : 1, y : 1})
        ], 0);
        if(playFire && this._midFire) {
            tl.add(() => {
                this._midFire.visible = true;
                this._midFire.state.setAnimation(0, "burning", false);
            }, 0);
            tl.add(() => {
                if(!this._destroyed) {
                    this._midFire.state.setAnimation(0, "loop", true);
                }
            }, this._midFire.spineData.findAnimation("burning").duration);
            tl.add(gsap.to(this.scale, {x : 1.1, y : 1.1, duration : 0.066}), 0.1);
            tl.add(gsap.to(this.scale, {x : 1, y : 1, duration : 0.2}), 0.167);
        }

        return tl;
    }

    protected getStateTint(state?:string):number {
        if(!state) {
            state = this._parts[0].currentSpriteName;
        }

        return state === TombstoneRipSymbolState.NO_WIN ? this._noWinTint : (state === TombstoneRipSymbolState.NO_WIN_HINT ? this._noWinHintTint : 0xffffff);
    }

    public playLanding(init:boolean = false):TimelineLite {
        const tl = new TimelineLite();
        tl.add(gsap.to(this, {duration : 0.01}));
        return tl;
    }

    public resetFire():void {
        if(this._midFire) {
            this._midFire.state.setEmptyAnimation(0, 0);
            this._midFire.visible = false;
        }
    }

    public get destroyed():boolean {
        return this._destroyed;
    }

    public playLandingState(duration:number):void {
        this.getStateAnimation(TombstoneRipSymbolState.NORMAL,
            {duration : duration, alpha : 1},
            {duration : duration, alpha : 0})
    }

    public playIdle():void {
        if(this._idlePlaying) {
            return;
        }

        this._idlePlaying = true;
        this._idle = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("symbolIdle"));
        this._idle.autoUpdate = true;
        this._idle.position.set(this._centerPosition.x, this._centerPosition.y);
        this._idle.visible = false;
        this.updateLayers();

        this._idleTl = new TimelineLite();
        this._idleTl.add(() => {this._idle!.state.setAnimation(0, "animation", false)});
        this._idleTl.add(() => {
            if(this._idle) {
                this._idle!.visible = true;
            }
        }, 0.1);
        this._idleTl.add(() => {
            if(this._idle) {
                SpineHelper.destroySpine(this._idle);
                this._idle = null;
            }
            this._idleTl = null;
            this._idlePlaying = false;
        }, this._idle.spineData.findAnimation("animation").duration);
    }

    public stopIdle():void {
        if(this._idleTl) {
            this._idleTl.pause();
            this._idleTl.kill();
            this._idleTl = null;
        }
        if(this._idle) {
            SpineHelper.destroySpine(this._idle);
            this._idle = null;
        }
        this._idlePlaying = false;
    }

    public showScatterFrame(animate:boolean = false):void {
        if(!this._scatterFrame) {
            this._scatterFrame = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures(`S1/S1_frame`)[0]);
            this._scatterFrame.anchor.set(0.5);
            this._scatterFrame.position.set(this._centerPosition.x, this._centerPosition.y);
        }
        this.updateLayers();
        if(animate) {
            const tl = gsap.timeline();
            tl.add([
                gsap.to(this._scatterFrame.scale, {x : 1.02, y : 1.02, duration : 0.05}),
                gsap.to(this._scatterFrame, {duration : 0.05, pixi : {colorize : "maroon", colorizeAmount : 0.8}})
            ]);
            tl.add([
                gsap.to(this._scatterFrame.scale, {x : 1, y : 1, duration : 0.3}),
                gsap.to(this._scatterFrame, {duration : 2, pixi : {colorize : "maroon", colorizeAmount : 0}})
            ]);
        }
    }
}